import MailHeader from '../MailHeader';
import back_arrow from "../../../assets/svg/back-arrow.svg";
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../../styles/MailTemplate.css';
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import CommonFooter from '../../../CommonFooter/CommonFooter';
import { ToastContainer, toast } from "react-toastify";
import axiosInstance from '../../../axiosInstance';
import { FiChevronDown } from "react-icons/fi";
import {
    font,
    fontSize,
    formatBlock,
    align,
    list,
    table,
    link,
    image,
    video,
    blockquote,
    lineHeight,
    fontColor,
    hiliteColor,
    textStyle
} from "suneditor/src/plugins";


const MailTemplateCreation = () => {
    const navigate = useNavigate();
    const [bccInput, setBccInput] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [ccInput, setCcInput] = useState('');
    const fileInputRef = useRef(null);
    const location = useLocation();
    const { state } = location;
    const isEditMode = state?.isEditMode || false;
    const editDataResponse = state?.editDataResponse || null;
    const mailId = state?.mailId || null;
    const [formData, setFormData] = useState({
        title: '',
        subject: '',
        status: 'Active',
        cc: [],
        bcc: [],
        message: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const isValidEmail = (email) => {
        const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.(com|org|in|tech|co|net|edu|io)$/;

        if (!regex.test(email)) return false;
        if (email.includes("..")) return false;
        if (email.endsWith(".")) return false;
        if (/[^a-zA-Z0-9._%+-@]/.test(email)) return false;
        return true;
    };

    const handleMailKeyDown = (e, type) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();

            const value = (type === 'cc' ? ccInput : bccInput)
                .trim()
                .replace(",", "");

            if (!value) return;

            if (!isValidEmail(value)) {
                toast.error("Invalid email format");
                return;
            }

            // ✅ check correct list (cc OR bcc)
            if (formData[type].includes(value)) {
                toast.error("Email already added");
                return;
            }

            // ✅ add to correct list
            setFormData(prev => ({
                ...prev,
                [type]: [...prev[type], value]
            }));

            // ✅ clear correct input
            type === 'cc' ? setCcInput('') : setBccInput('');
        }
    };

    const attachmentPlugin = {
        name: "attachment",
        display: "command",
        title: "Attach file",
        innerHTML: "📎",
        add: function (core) {
            core.context.attachment = {};
        },

        action: function () {
            fileInputRef.current.click();
        }
    };

    const MAX_SIZE = 10 * 1024 * 1024;
    const ALLOWED_TYPES = ["pdf", "doc", "docx", "xls", "xlsx", "csv", "png", "jpg", "jpeg"];

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        setAttachments(prev => {
            const existingNames = prev.map(f => f.name);

            const validFiles = files.filter(file => {
                const ext = file.name.split(".").pop().toLowerCase();

                if (!ALLOWED_TYPES.includes(ext)) {
                    toast.error(`${file.name} is not allowed`);
                    return false;
                }

                if (file.size > MAX_SIZE) {
                    toast.error(`${file.name} exceeds 10MB`);
                    return false;
                }

                if (existingNames.includes(file.name)) {
                    toast.error(`${file.name} already added`);
                    return false;
                }

                return true;
            });

            return [...prev, ...validFiles];
        });

        e.target.value = "";
    };

    const validateForm = () => {
        if (!formData.title.trim()) {
            toast.error("Title is required");
            return false;
        }

        if (!formData.subject.trim()) {
            toast.error("Subject is required");
            return false;
        }

        if (!formData.status) {
            toast.error("Status is required");
            return false;
        }
        const plainMessage = formData.message
            .replace(/<[^>]*>/g, "")
            .trim();

        if (!plainMessage) {
            toast.error("Message content is required");
            return false;
        }

        return true;
    };


    const handleSave = async () => {
        if (!validateForm()) return;
        try {
            const payload = {
                title: formData.title,
                subject: formData.subject,
                cc_email: formData.cc.join(","),
                bcc_email: formData.bcc.join(","),
                content: formData.message,
                status: formData.status === "Active" ? 1 : 0,
            };

            const res = await axiosInstance.post("/mail_template/save", payload);

            if (res.data.success) {
                toast.success(res.data.message || "Mail Template Created Successfully");
                navigate(-1);
            } else {
                toast.error(res.data.message || "Something Went Wrong");
            }
        } catch (error) {
            toast.error("Save failed");
        }
    };

    const handleUpdate = async () => {
        if (!validateForm()) return;
        try {
            const payload = {
                id: mailId,
                title: formData.title,
                subject: formData.subject,
                cc_email: formData.cc.join(","),
                bcc_email: formData.bcc.join(","),
                content: formData.message,
                status: formData.status === "Active" ? 1 : 0,
            };

            const res = await axiosInstance.put("/mail_template/update", payload);

            if (res.data.success) {
                toast.success(res.data.message || "Mail Template Updated Successfully");
                navigate(-1);
            } else {
                toast.error(res.data.message || "Something Went Wrong");
            }
        } catch (error) {
            toast.error("Update failed");
        }
    };

    useEffect(() => {
        if (isEditMode && editDataResponse?.Mail_template) {
            const t = editDataResponse.Mail_template;

            setFormData({
                title: t.title || "",
                subject: t.subject || "",
                status: t.status ? "Active" : "Inactive",
                cc: t.cc_email ? t.cc_email.split(",") : [],
                bcc: t.bcc_email ? t.bcc_email.split(",") : [],
                message: t.content || ""
            });
        }
    }, [isEditMode, editDataResponse]);


    return (
        <>
            <MailHeader />

            {/* Header */}
            <div className="invoice-module-header">
                <div className="invoice-module-header-left">
                    <img
                        src={back_arrow}
                        alt="Back"
                        onClick={() => navigate(-1)}
                        style={{ cursor: "pointer" }}
                    />
                    <p className="new_job" style={{ fontSize: '14px' }}>
                        {isEditMode ? "Edit Mail Template" : "New Mail Template"}
                    </p>

                </div>

                <div className="invoice-module_header_right">
                    <button className="edit_btn" onClick={() => navigate(-1)}>Cancel</button>
                    <button
                        className="edit_btn"
                        onClick={isEditMode ? handleUpdate : handleSave}
                    >
                        {isEditMode ? "Update" : "Save"}
                    </button>
                </div>
            </div>

            <div className="mailtemplate-creation-container">
                <p className="mailtemplate-creation-header">Template details</p>

                {/* Row 1 */}
                <div className="mailtemplate-creation-row">
                    <div className="mailtemplate-creation-field title">
                        <label>Title <span style={{ color: 'red' }}>*</span></label>
                        <input
                            type="text"
                            name="title"
                            placeholder="Enter here"
                            value={formData.title}
                            disabled={isEditMode} 
                            onChange={handleChange}
                            style={{ margin: '0px' }}
                        />
                    </div>

                    <div className="mailtemplate-creation-field subject">
                        <label>Subject <span style={{ color: 'red' }}>*</span></label>
                        <input
                            type="text"
                            name="subject"
                            placeholder="Enter here"
                            value={formData.subject}
                            onChange={handleChange}
                            style={{ margin: '0px' }}
                        />
                    </div>

                    <div className="mailtemplate-creation-field status">
                        <label>Status <span style={{ color: 'red' }}>*</span></label>
                        <div className={`status-select ${formData.status.toLowerCase()}`}>
                            <span className="status-dot"></span>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                            <FiChevronDown className="dropdown-icon" />
                        </div>
                    </div>
                </div>

                {/* BCC */}
                <div className="mailtemplate-creation-row">
                    {/* CC */}
                    <div className="mailtemplate-creation-field half">
                        <label>CC</label>
                        <div className="bcc-input-wrapper">
                            {formData.cc.map((email, index) => (
                                <span className="bcc-pill" key={index}>
                                    {email}
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setFormData(prev => ({
                                                ...prev,
                                                cc: prev.cc.filter((_, i) => i !== index)
                                            }))
                                        }
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}

                            <input
                                type="text"
                                placeholder="Add email and press Enter"
                                value={ccInput}
                                onChange={(e) => setCcInput(e.target.value)}
                                onKeyDown={(e) => handleMailKeyDown(e, 'cc')}
                                style={{ margin: '0px' }}
                            />
                        </div>
                    </div>

                    {/* BCC */}
                    <div className="mailtemplate-creation-field half">
                        <label>BCC</label>
                        <div className="bcc-input-wrapper">
                            {formData.bcc.map((email, index) => (
                                <span className="bcc-pill" key={index}>
                                    {email}
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setFormData(prev => ({
                                                ...prev,
                                                bcc: prev.bcc.filter((_, i) => i !== index)
                                            }))
                                        }
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}

                            <input
                                type="text"
                                placeholder="Add email and press Enter"
                                value={bccInput}
                                onChange={(e) => setBccInput(e.target.value)}
                                onKeyDown={(e) => handleMailKeyDown(e, 'bcc')}
                                style={{ margin: '0px' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Message */}
                <div className="mailtemplate-creation-message">
                    <label>Message <span style={{ color: 'red' }}>*</span></label>

                    <SunEditor
                        setContents={formData.message}
                        onChange={(content) =>
                            setFormData(prev => ({ ...prev, message: content }))
                        }
                        setOptions={{
                            fontSizeUnit: "px",
                            defaultStyle: "font-family: Arial; font-size: 14px;",
                            formats: ["p", "div", "span"],
                            minHeight: "200px",
                            placeholder: "Compose your email...",
                            plugins: [
                                font,
                                fontSize,
                                formatBlock,
                                align,
                                list,
                                table,
                                link,
                                image,
                                video,
                                blockquote,
                                lineHeight,
                                fontColor,
                                hiliteColor,
                                textStyle,
                                attachmentPlugin
                            ],

                            font: [
                                "Arial", "Helvetica", "Times New Roman", "Verdana",
                                "Georgia", "Trebuchet MS", "Tahoma",
                                "Courier New", "Lucida Sans", "Palatino Linotype", "Comic Sans MS"
                            ],
                            fontSize: [10, 12, 14, 16, 18, 20, 24],

                            imageUpload: true,
                            imageResizing: true,
                            imageWidth: "auto",
                            table: true,
                            videoFileInput: false,

                            buttonList: [
                                ["undo", "redo"],
                                ["font", "fontSize", "lineHeight"],
                                ["bold", "italic", "underline", "strike"],
                                ["fontColor", "hiliteColor"],
                                ["align"],
                                ["blockquote"],
                                ["list"],
                                ["table"],
                                ["link", "image", "attachment"],
                                ["removeFormat"],
                                ["fullScreen", "codeView"]
                            ]
                        }}
                    />
                    {attachments.length > 0 && (
                        <div className="attachment-pill-container">
                            {attachments.map((file, index) => (
                                <div className="attachment-pill" key={index}>
                                    <span className="attachment-name" title={file.name}>
                                        📎 {file.name}
                                    </span>
                                    <button
                                        type="button"
                                        className="attachment-remove"
                                        onClick={() =>
                                            setAttachments(prev =>
                                                prev.filter((_, i) => i !== index)
                                            )
                                        }
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                    />
                </div>
            </div>

            <CommonFooter />
            <ToastContainer position="top-right" autoClose={3000} />
        </>
    );
};

export default MailTemplateCreation;
